import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DocumentMagnifyingGlassIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { getSCGRuleSourceInfo } from '../services/api';
import { buildPdfSandboxUrl, pdfHighlightMode } from '../utils/pdfSandboxUrl';

const PURPLE = '#7e22ce';
const PURPLE_HOVER = '#581c87';

/**
 * Universal "view source in PDF" affordance for SCG rule citations.
 *
 * Props:
 *   ruleId         — required. Raw SCG rule ID.
 *   sourceInfo     — optional. If provided, skips the fetch. Accepts either
 *                    a flat rule object (with source_document + location_metadata)
 *                    or the blob returned by /scg/rule-source-info.
 *   variant        — 'icon' (default) | 'icon-text' | 'text-link'
 *   size           — 'sm' (default) | 'md'
 *   stopPropagation — boolean, default true. Useful when nested inside a clickable row.
 *
 * Hidden entirely when the rule has no source PDF.
 */
export default function ViewInPdfButton({
  ruleId,
  sourceInfo,
  variant = 'icon',
  size = 'sm',
  stopPropagation = true,
  className = '',
}) {
  // Auto-fetch if not provided. React Query dedupes by ruleId so multiple
  // ViewInPdfButtons on the same page collapse to one network call per rule.
  const { data: fetched } = useQuery({
    queryKey: ['scg-source', ruleId],
    queryFn: () => getSCGRuleSourceInfo(ruleId),
    enabled: !!ruleId && !sourceInfo,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const effective = sourceInfo || fetched;
  const url = buildPdfSandboxUrl(ruleId, effective);
  const mode = pdfHighlightMode(effective);

  if (!url || !mode) return null;

  const sizePx = size === 'md' ? 18 : 14;
  const title = 'View source in PDF — jumps to the rule’s page and highlights its text';

  const handleClick = (e) => {
    if (stopPropagation) e.stopPropagation();
  };

  if (variant === 'text-link') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        title={title}
        className={`inline-flex items-center gap-1 ${className}`}
        style={{
          fontSize: size === 'md' ? '12px' : '11px',
          color: PURPLE,
          textDecoration: 'none',
        }}
        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
      >
        <DocumentMagnifyingGlassIcon style={{ width: sizePx, height: sizePx }} />
        View in PDF
        <ArrowTopRightOnSquareIcon style={{ width: sizePx - 2, height: sizePx - 2 }} />
      </a>
    );
  }

  // 'icon' (default) and 'icon-text' both use the icon button pattern
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      title={title}
      className={`inline-flex items-center gap-1 ${className}`}
      style={{
        color: PURPLE,
        textDecoration: 'none',
        padding: '2px',
      }}
      onMouseOver={(e) => { e.currentTarget.style.color = PURPLE_HOVER; }}
      onMouseOut={(e) => { e.currentTarget.style.color = PURPLE; }}
    >
      <DocumentMagnifyingGlassIcon style={{ width: sizePx, height: sizePx }} />
      {variant === 'icon-text' && (
        <span style={{ fontSize: size === 'md' ? '12px' : '11px' }}>PDF</span>
      )}
    </a>
  );
}
